"use client"
import { useEffect, useState } from 'react';
import {
    Box, Flex, Text, Tooltip, Popover, IconButton, Portal
} from '@chakra-ui/react';
import { useColorModeValue } from './ui/color-mode';
import { LuCircleHelp } from 'react-icons/lu';

/**
 * Format period end date and form type into a human-readable source label
 * @param {string} periodEnd - e.g., "2024-09-30"
 * @param {string} form - e.g., "10-K" or "10-Q"
 * @param {string} fiscalPeriod - e.g., "Q1", "Q2", "Q3", "FY"
 * @param {number} fiscalYear - e.g., 2024
 */
function formatSourceLabel(periodEnd, form, fiscalPeriod, fiscalYear) {
    if (!periodEnd && !form) return null;
    
    let periodLabel = '';
    if (fiscalYear && fiscalPeriod) {
        if (fiscalPeriod === 'FY') {
            periodLabel = `FY${fiscalYear}`;
        } else {
            periodLabel = `${fiscalPeriod} ${fiscalYear}`;
        }
    } else if (periodEnd) {
        // Fallback to parsing the date
        const date = new Date(periodEnd + 'T00:00:00');
        const month = date.getMonth();
        const year = date.getFullYear();
        // Rough quarter mapping
        if (month <= 2) periodLabel = `Q1 ${year}`;
        else if (month <= 5) periodLabel = `Q2 ${year}`;
        else if (month <= 8) periodLabel = `Q3 ${year}`;
        else periodLabel = `Q4 ${year}`;
    }
    
    const formLabel = form === '10-K' ? '10-K' : form === '10-Q' ? '10-Q' : form;
    return periodLabel && formLabel ? `${periodLabel} ${formLabel}` : (periodLabel || formLabel);
}

function usePrefersHover() {
    const [prefersHover, setPrefersHover] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return true;
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const media = window.matchMedia('(hover: hover) and (pointer: fine)');
        const sync = () => setPrefersHover(media.matches);
        sync();
        media.addEventListener('change', sync);
        return () => media.removeEventListener('change', sync);
    }, []);

    return prefersHover;
}

function CalculationTooltipBody({
    title,
    value1,
    value2,
    result,
    breakdown,
    resultLabel,
    resultFormatAs,
    resultLabelColor,
    sourceInfo,
    equation,
    note,
    tooltipText,
    tooltipNumber,
    tooltipResult,
    tooltipDivider,
}) {
    const getOperationLabel = () => {
        if (!value2) return null;
        if (value2.operation === 'multiply') return 'Times:';
        if (value2.operation === 'subtract') return 'Less:';
        if (value2.operation === 'add') return 'Plus:';
        return null;
    };

    const formatValue = (value, formatAs = 'currency') => {
        if (value === null || value === undefined) return null;
        if (formatAs === 'number') {
            return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
        }
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const formatResult = (resultValue, formatAs = 'currency') => {
        if (resultValue === null || resultValue === undefined) return null;
        if (formatAs === 'number') {
            return resultValue.toLocaleString(undefined, { maximumFractionDigits: 0 });
        }
        return resultValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const prefixForValue = (formatAs) => (formatAs === 'number' ? '' : '$');

    return (
        <Box>
            <Text fontWeight="bold" mb={2} fontSize="sm" color={tooltipText} textAlign="center" display="block" width="100%">
                {title}
            </Text>
            {equation?.length > 0 && (
                <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    textAlign="center"
                    mb={3}
                    data-testid="calculation-tooltip-equation"
                >
                    {equation.map((part, index) => (
                        <Text
                            as="span"
                            key={`${part.text}-${index}`}
                            color={part.color || tooltipText}
                        >
                            {part.text}
                        </Text>
                    ))}
                </Text>
            )}
            {note && (
                <Box
                    mb={3}
                    px={2.5}
                    py={2}
                    borderRadius="md"
                    bg="orange.50"
                    border="1px solid"
                    borderColor="orange.200"
                    data-testid="calculation-tooltip-note"
                >
                    <Text fontSize="xs" fontWeight="semibold" color="orange.800" textAlign="center">
                        {note}
                    </Text>
                </Box>
            )}
            <Box fontSize="sm" fontFamily="mono">
                {breakdown?.length > 0 && breakdown.map((item) => (
                    item.value !== null && item.value !== undefined && (
                        <Flex key={item.label} justifyContent="space-between" alignItems="flex-end" mb={1}>
                            <Text color={item.labelColor || tooltipText} flex="1" mr={2} lineHeight="1.2">
                                {item.label}
                            </Text>
                            <Box
                                flex="1"
                                borderBottom="1px dotted"
                                borderColor={tooltipDivider}
                                mx={1}
                                mb="0.15em"
                                minH="1px"
                            />
                            <Text color={tooltipNumber} textAlign="right" whiteSpace="nowrap" lineHeight="1.2">
                                {prefixForValue(item.formatAs)}{formatValue(item.value, item.formatAs)}
                            </Text>
                        </Flex>
                    )
                ))}
                {!breakdown?.length && value1 && value1.value !== null && (
                    <Flex justifyContent="space-between" alignItems="flex-end" mb={1}>
                        <Text color={value1.labelColor || tooltipText} flex="1" mr={2} lineHeight="1.2">
                            {value1.label}
                        </Text>
                        <Box
                            flex="1"
                            borderBottom="1px dotted"
                            borderColor={tooltipDivider}
                            mx={1}
                            mb="0.15em"
                            minH="1px"
                        />
                        <Text color={tooltipNumber} textAlign="right" whiteSpace="nowrap" lineHeight="1.2">
                            {prefixForValue(value1.formatAs)}{formatValue(value1.value, value1.formatAs)}
                        </Text>
                    </Flex>
                )}
                {value2 && value2.value !== null && (
                    <Flex justifyContent="space-between" alignItems="flex-end" mb={1}>
                        <Text color={value2.labelColor || tooltipText} flex="1" mr={2} lineHeight="1.2">
                            {getOperationLabel()} {value2.label}
                        </Text>
                        <Box
                            flex="1"
                            borderBottom="1px dotted"
                            borderColor={tooltipDivider}
                            mx={1}
                            mb="0.15em"
                            minH="1px"
                        />
                        <Text color={tooltipNumber} textAlign="right" whiteSpace="nowrap" lineHeight="1.2">
                            {value2.operation === 'subtract'
                                ? `(${prefixForValue(value2.formatAs)}${formatValue(value2.value, value2.formatAs)})`
                                : value2.operation === 'multiply'
                                ? formatValue(value2.value, value2.formatAs || 'number')
                                : `${prefixForValue(value2.formatAs)}${formatValue(value2.value, value2.formatAs)}`
                            }
                        </Text>
                    </Flex>
                )}
                {result !== null && result !== undefined && (
                    <Flex justifyContent="space-between" alignItems="flex-end" mt={2} pt={1} borderTop="1px solid" borderColor={tooltipDivider}>
                        <Text
                            fontWeight="bold"
                            color={resultLabelColor || tooltipText}
                            flex="1"
                            mr={2}
                            lineHeight="1.2"
                        >
                            {resultLabel || (breakdown?.length ? `Total ${title.toLowerCase()}` : title)}
                        </Text>
                        <Box
                            flex="1"
                            borderBottom="1px dotted"
                            borderColor={tooltipDivider}
                            mx={1}
                            mb="0.15em"
                            minH="1px"
                        />
                        <Text
                            fontWeight="bold"
                            color={tooltipResult}
                            textAlign="right"
                            whiteSpace="nowrap"
                            lineHeight="1.2"
                        >
                            {prefixForValue(resultFormatAs)}{formatResult(result, resultFormatAs)}
                        </Text>
                    </Flex>
                )}
                {sourceInfo && (sourceInfo.periodEnd || sourceInfo.form || sourceInfo.gaapKeyLabel) && (
                    <Box mt={3} pt={2} borderTop="1px dashed" borderColor={tooltipDivider}>
                        <Text fontSize="xs" color={tooltipText} textAlign="center">
                            Source: {[
                                sourceInfo.gaapKeyLabel,
                                formatSourceLabel(
                                    sourceInfo.periodEnd,
                                    sourceInfo.form,
                                    sourceInfo.fiscalPeriod,
                                    sourceInfo.fiscalYear,
                                ),
                            ].filter(Boolean).join(' from ')}
                            {sourceInfo.filingUrl && (
                                <>
                                    {' '}
                                    <a
                                        href={sourceInfo.filingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#3182ce', textDecoration: 'underline' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        View Filing →
                                    </a>
                                </>
                            )}
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

/**
 * Reusable calculation tooltip component for displaying financial calculations
 * @param {string} title - The title/category of the calculation (e.g., "Net debt", "Market Cap")
 * @param {Object} value1 - First value: { label: string, value: number }
 * @param {Object} value2 - Second value (optional): { label: string, value: number, operation: 'subtract' | 'multiply' | 'add' }
 * @param {number} result - The calculated result
 * @param {Array<{label: string, value: number, formatAs?: string}>} breakdown - Optional line items shown before the result
 * @param {string} resultLabel - Optional result-row label override
 * @param {string} resultFormatAs - Format for result row: 'currency' (default) or 'number'
 * @param {string} resultLabelColor - Optional color for the result label
 * @param {Array<{text: string, color?: string}>} equation - Optional color-coded equation shown under the title
 * @param {string} note - Optional contextual note shown above the calculation
 */
export function CalculationTooltip({
    title,
    value1,
    value2,
    result,
    breakdown,
    resultLabel,
    resultFormatAs = 'currency',
    resultLabelColor,
    sourceInfo,
    equation,
    note,
    onOpenChange,
}) {
    const prefersHover = usePrefersHover();
    const [clickOpen, setClickOpen] = useState(false);

    // Color mode values for tooltip
    const tooltipBg = useColorModeValue('white', 'gray.800');
    const tooltipColor = useColorModeValue('gray.800', 'gray.100');
    const tooltipBorder = useColorModeValue('gray.200', 'gray.600');
    const tooltipText = useColorModeValue('gray.700', 'gray.300');
    const tooltipNumber = useColorModeValue('gray.800', 'gray.100');
    const tooltipResult = useColorModeValue('gray.900', 'white');
    const tooltipDivider = useColorModeValue('gray.400', 'gray.500');

    const handleOpenChange = (open) => {
        onOpenChange?.(open);
    };

    const contentProps = {
        bg: tooltipBg,
        color: tooltipColor,
        p: 3,
        borderRadius: 'md',
        boxShadow: 'lg',
        border: '1px solid',
        borderColor: tooltipBorder,
        maxW: '350px',
    };

    const body = (
        <CalculationTooltipBody
            title={title}
            value1={value1}
            value2={value2}
            result={result}
            breakdown={breakdown}
            resultLabel={resultLabel}
            resultFormatAs={resultFormatAs}
            resultLabelColor={resultLabelColor}
            sourceInfo={sourceInfo}
            equation={equation}
            note={note}
            tooltipText={tooltipText}
            tooltipNumber={tooltipNumber}
            tooltipResult={tooltipResult}
            tooltipDivider={tooltipDivider}
        />
    );

    const trigger = (
        <IconButton
            size="sm"
            variant="ghost"
            aria-label={`${title} calculation info`}
            p={0}
        >
            <LuCircleHelp />
        </IconButton>
    );

    // Touch / coarse pointers: click to open, click again or outside to close.
    if (!prefersHover) {
        return (
            <Popover.Root
                open={clickOpen}
                onOpenChange={({ open }) => {
                    setClickOpen(open);
                    handleOpenChange(open);
                }}
                positioning={{ placement: 'top', gutter: 8 }}
            >
                <Popover.Trigger asChild>
                    {trigger}
                </Popover.Trigger>
                <Portal>
                    <Popover.Positioner zIndex="tooltip">
                        <Popover.Content {...contentProps}>
                            <Popover.Arrow>
                                <Popover.ArrowTip />
                            </Popover.Arrow>
                            {body}
                        </Popover.Content>
                    </Popover.Positioner>
                </Portal>
            </Popover.Root>
        );
    }

    return (
        <Tooltip.Root
            openDelay={0}
            closeDelay={200}
            interactive
            onOpenChange={({ open }) => handleOpenChange(open)}
        >
            <Tooltip.Trigger asChild>
                {trigger}
            </Tooltip.Trigger>
            <Portal>
                <Tooltip.Positioner zIndex="tooltip">
                    <Tooltip.Content {...contentProps}>
                        {body}
                    </Tooltip.Content>
                </Tooltip.Positioner>
            </Portal>
        </Tooltip.Root>
    );
}
